import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSearch } from '@react-icons/all-files/fa/FaSearch';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import ProfaseModal from '../../commons/ProfaseModal';
import { ConfigButton } from '../../commons/DataGridActions';

interface WorkflowSearchItem {
  id: number;
  nombre?: string;
  workflowClase?: { nombre?: string };
}

interface WorkflowSearchModalProps {
  open: boolean;
  currentWorkflowId?: string;
  onClose: () => void;
  onNavigateToConfig?: (path: string) => void;
}

function parseWorkflowsResponse(data: unknown): WorkflowSearchItem[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.content)) return o.content as WorkflowSearchItem[];
    if (Array.isArray(o.data)) return o.data as WorkflowSearchItem[];
  }
  return [];
}

function workflowApiQuery(input: string): string {
  const q = input.trim();
  return q || '%';
}

export default function WorkflowSearchModal({
  open,
  currentWorkflowId,
  onClose,
  onNavigateToConfig,
}: WorkflowSearchModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WorkflowSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setLoading(false);
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      axios
        .get('api/profase-workflows', {
          params: { query: workflowApiQuery(query), page: 0, size: 20, sort: 'nombre,asc' },
          signal: controller.signal,
        })
        .then(res => {
          if (!controller.signal.aborted) {
            setResults(parseWorkflowsResponse(res.data));
          }
        })
        .catch(err => {
          if (!axios.isCancel(err) && !controller.signal.aborted) {
            setResults([]);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  const handleConfigure = (item: WorkflowSearchItem) => {
    onClose();
    if (String(item.id) !== String(currentWorkflowId)) {
      const path = `/workflow/${item.id}/config`;
      if (onNavigateToConfig) onNavigateToConfig(path);
      else navigate(path);
    }
  };

  return (
    <ProfaseModal
      open={open}
      titleId="workflow-search-modal-title"
      size="wide"
    >
      <div style={s.header}>
        <h2 id="workflow-search-modal-title" className="profase-modal-title m-0">
          <FaSearch style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Buscar workflow
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>

      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Buscar por nombre…"
        style={s.input}
        aria-label="Buscar workflow por nombre"
        autoComplete="off"
      />

      <div style={s.listBox}>
        {loading ? (
          <div style={s.empty}>
            <FaSpinner style={{ animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
            Buscando…
          </div>
        ) : results.length === 0 ? (
          <div style={s.empty}>Sin resultados</div>
        ) : (
          results.map(item => {
            const isCurrent = String(item.id) === String(currentWorkflowId);
            return (
              <div
                key={item.id}
                style={{
                  ...s.listItem,
                  ...(isCurrent ? s.listItemCurrent : {}),
                }}
              >
                <button
                  type="button"
                  style={s.listItemInfoBtn}
                  onClick={() => handleConfigure(item)}
                  title="Abrir configuración de plantilla"
                >
                  <span style={s.listItemName}>{item.nombre ?? `Workflow #${item.id}`}</span>
                  {item.workflowClase?.nombre && (
                    <span style={s.listItemClase}>{item.workflowClase.nombre}</span>
                  )}
                </button>
                <ConfigButton
                  title="Configurar plantilla"
                  onClick={() => handleConfigure(item)}
                />
              </div>
            );
          })
        )}
      </div>
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
    background: 'var(--crm-input-bg, #fff)',
    color: 'var(--crm-text-primary, #0f172a)',
    boxSizing: 'border-box',
    marginBottom: 12,
  },
  listBox: {
    maxHeight: 360,
    overflowY: 'auto',
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 10,
    background: 'var(--crm-surface, #fff)',
    padding: 6,
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    width: '100%',
    padding: '8px 10px',
    border: '1px solid transparent',
    borderRadius: 8,
    background: 'transparent',
    color: 'var(--crm-text-primary, #0f172a)',
  },
  listItemInfoBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    minWidth: 0,
    flex: 1,
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  listItemCurrent: {
    background: 'var(--crm-panel-list-active-bg, rgba(16, 185, 129, 0.08))',
    borderColor: 'var(--crm-panel-list-active-border, #10b981)',
  },
  listItemName: {
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationColor: 'transparent',
    transition: 'text-decoration-color 0.15s ease',
  },
  listItemClase: {
    fontSize: 12,
    color: '#94a3b8',
  },
  empty: {
    padding: '24px 12px',
    fontSize: 13,
    color: '#94a3b8',
    fontStyle: 'italic',
    textAlign: 'center',
  },
};
